import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Compass, Sparkles, Download, ChevronRight, Link2, Check, Paperclip, Search, Plus, Loader2, PenLine } from 'lucide-react';
import { parseSheet } from '../lib/sheet';
import {
  api,
  type Overview,
  type ThemeDetail,
  type EntityDetail,
  type GrainDetail,
  type ThemeRow,
  type EntityRow,
  type GrainCard,
} from '../lib/api';
import { OwnershipBadge, Chip, Spinner } from './ui';
import { useFlyTranslate, TranslateButton } from './Translate';
import { OpenQuestions } from './OpenQuestions';
import { Chronicle } from './Chronicle';
import { Contradictions } from './Contradictions';
import { themeMarkdown, entityMarkdown, downloadMarkdown } from '../lib/export';

/** Ouvre le composeur de capture pré-rempli pour répondre à un trou ou lever une
 *  contradiction (PRO) ; la résolution écarte l'élément d'origine une fois capturé. */
type OnCapture = (
  subject: string,
  question: string,
  tag?: string,
  resolve?: { kind: 'question' | 'contradiction'; id: string },
) => void;

/* ---------- routage par hash ---------- */
type Route =
  | { view: 'overview'; id?: undefined }
  | { view: 'theme' | 'entity' | 'grain' | 'author'; id: string };
function parseHash(): Route {
  const m = location.hash.match(/^#\/(theme|entity|grain|author)\/(.+)$/);
  return m ? { view: m[1] as 'theme' | 'entity' | 'grain' | 'author', id: decodeURIComponent(m[2]!) } : { view: 'overview' };
}
const nav = (to: string): void => {
  location.hash = to;
};
function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(parseHash);
  useEffect(() => {
    const on = (): void => setRoute(parseHash());
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);
  return route;
}

/* ---------- briques ---------- */
function GrainList({ grains }: { grains: GrainCard[] }) {
  return (
    <div className="space-y-2">
      {grains.map((g) => (
        <button
          key={g.id}
          onClick={() => nav(`#/grain/${g.id}`)}
          className="block w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-left transition hover:border-accent"
        >
          <p className="text-sm font-medium">{g.title}</p>
          <p className="mt-0.5 text-sm text-muted">{g.excerpt}</p>
        </button>
      ))}
    </div>
  );
}

/** Bouton « copier le lien » : copie l'URL courante (deep-link Explorer). */
function ShareButton() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard
      .writeText(location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => {});
  };
  return (
    <button
      onClick={copy}
      title={t('explore.copyLink')}
      className="flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-1.5 text-sm text-fg hover:bg-accent hover:text-accent-fg"
    >
      {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      <span className="hidden sm:inline">{copied ? t('explore.copied') : t('explore.copyLink')}</span>
    </button>
  );
}

/* ---------- détail d'un grain (deep-link partageable) ---------- */
function GrainView({ id }: { id: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<GrainDetail | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api.browse
      .grain(id)
      .then(setData)
      .catch(() => setError(true));
  }, [id]);

  // Traduction à la volée du grain s'il est dans une autre langue que l'interface (TRAD).
  const tr = useFlyTranslate(data?.bodyClean ?? '', data?.lang);

  if (error) return <p className="text-muted">{t('error.generic')}</p>;
  if (!data) return <Spinner label={t('explore.loading')} />;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted">{t('explore.grain')}</p>
          <h2 className="text-xl font-semibold">{data.title}</h2>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {tr.available && <TranslateButton busy={tr.busy} on={tr.on} onClick={tr.toggle} />}
          <ShareButton />
        </div>
      </div>

      <div className="md leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{tr.display}</ReactMarkdown>
      </div>

      {data.attachments && data.attachments.length > 0 && (
        <Section title={t('explore.sources')}>
          <div className="flex flex-wrap gap-1.5">
            {data.attachments.map((a) => (
              <a
                key={a.id}
                href={api.attachmentUrl(data.id, a.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md bg-surface-2 px-2 py-1 text-xs text-muted hover:text-fg"
              >
                <Paperclip className="h-3 w-3" /> <span className="max-w-[12rem] truncate">{a.filename}</span>
              </a>
            ))}
          </div>
        </Section>
      )}

      {data.entities.length > 0 && (
        <Section title={t('explore.relatedEntities')}>
          <div className="flex flex-wrap gap-1.5">
            {data.entities.map((e) => (
              <button key={e.entity.id} onClick={() => nav(`#/entity/${e.entity.id}`)} className="transition hover:opacity-80">
                <OwnershipBadge ownership={e.entity.ownership} label={e.entity.name} />
              </button>
            ))}
          </div>
        </Section>
      )}

      {data.themes.length > 0 && (
        <Section title={t('explore.themesOf')}>
          <div className="flex flex-wrap gap-1.5">
            {data.themes.map((th) => (
              <button
                key={th.theme.id}
                onClick={() => nav(`#/theme/${th.theme.id}`)}
                className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-fg transition hover:bg-accent hover:text-accent-fg"
              >
                # {th.theme.label}
              </button>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/* ---------- page « capturé par X » (CHAT-06) : tout ce qu'une personne a capturé ---------- */
const AUTHOR_PAGE = 10;
function AuthorView({ id }: { id: string }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [items, setItems] = useState<{ id: string; title: string; bodyClean: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [baseTotal, setBaseTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);

  useEffect(() => {
    const h = setTimeout(() => setDq(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void api
      .authorNotes(id, { q: dq, limit: AUTHOR_PAGE, offset: 0 })
      .then((res) => {
        if (!alive) return;
        setName(res.author.displayName);
        setItems(res.items.map((n) => ({ id: n.id, title: n.title, bodyClean: n.bodyClean })));
        setTotal(res.total);
        if (!dq) setBaseTotal(res.total);
      })
      .catch(() => alive && setBaseTotal(0))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, dq]);

  const loadMore = async (): Promise<void> => {
    setMore(true);
    const res = await api.authorNotes(id, { q: dq, limit: AUTHOR_PAGE, offset: items.length }).catch(() => null);
    if (res) {
      setItems((prev) => [...prev, ...res.items.map((n) => ({ id: n.id, title: n.title, bodyClean: n.bodyClean }))]);
      setTotal(res.total);
    }
    setMore(false);
  };

  const remaining = total - items.length;
  return (
    <div className="space-y-4">
      <div>
        <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted">
          <PenLine className="h-3.5 w-3.5" /> {t('source.capturedBadge')}
        </p>
        <h2 className="mt-0.5 text-xl font-semibold">{name || '…'}</h2>
        {baseTotal != null && <p className="mt-0.5 text-sm text-muted">{t('author.count', { count: baseTotal })}</p>}
      </div>

      {(baseTotal == null || baseTotal > 0 || dq.length > 0) && baseTotal !== 0 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('author.search')}
            className="w-full rounded-xl bg-surface py-2 pl-9 pr-4 text-sm ring-1 ring-border outline-none placeholder:text-muted focus:ring-accent"
          />
        </div>
      )}

      {loading ? (
        <Spinner label={t('explore.loading')} />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">{dq ? t('capture.noMatch', { q: dq }) : t('author.empty')}</p>
      ) : (
        <>
          <GrainList grains={items.map((n) => ({ id: n.id, title: n.title, excerpt: n.bodyClean.slice(0, 160) })) as GrainCard[]} />
          {remaining > 0 && (
            <button
              onClick={loadMore}
              disabled={more}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium text-accent transition hover:bg-surface disabled:opacity-60"
            >
              {more ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('capture.more', { count: Math.min(AUTHOR_PAGE, remaining) })}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function EntityChips({ entities }: { entities: EntityRow[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {entities.map((e) => (
        <button key={e.id} onClick={() => nav(`#/entity/${e.id}`)} className="transition hover:opacity-80">
          <OwnershipBadge ownership={e.ownership} label={`${e.name} · ${e.n}`} />
        </button>
      ))}
    </div>
  );
}

function ThemeChips({ themes }: { themes: ThemeRow[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {themes.map((t) => (
        <button
          key={t.id}
          onClick={() => nav(`#/theme/${t.id}`)}
          className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-fg transition hover:bg-accent hover:text-accent-fg"
        >
          # {t.label} <span className="text-muted">· {t.n}</span>
        </button>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      {children}
    </section>
  );
}

/* ---------- vue d'ensemble ---------- */
function Retry({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center">
      <p className="text-muted">{t('error.network')}</p>
      <button onClick={onRetry} className="rounded-lg bg-accent px-4 py-2 text-sm text-accent-fg">
        {t('error.retry')}
      </button>
    </div>
  );
}

function OverviewView({ onCapture }: { onCapture: OnCapture }) {
  const { t } = useTranslation();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setError(false);
    setData(null);
    api.browse.overview().then(setData).catch(() => setError(true));
  }, []);
  useEffect(() => load(), [load]);
  if (error) return <Retry onRetry={load} />;
  if (!data) return <Spinner label={t('explore.loading')} />;

  const max = Math.max(...data.topThemes.map((x) => x.n), 1);
  const size = (n: number): string => {
    const r = n / max;
    return r > 0.66 ? 'text-lg' : r > 0.33 ? 'text-base' : 'text-sm';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(['grains', 'entities', 'themes', 'docs'] as const).map((k) => (
          <div key={k} className="rounded-xl bg-surface px-3 py-2.5 ring-1 ring-border">
            <p className="text-xl font-semibold">{data.stats[k].toLocaleString('fr-CH')}</p>
            <p className="text-xs text-muted">{t(`explore.stats.${k}`)}</p>
          </div>
        ))}
      </div>

      <Section title={t('explore.themes')}>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
          {data.topThemes.map((th) => (
            <button
              key={th.id}
              onClick={() => nav(`#/theme/${th.id}`)}
              className={`font-medium text-fg/80 transition hover:text-accent ${size(th.n)}`}
              title={`${th.n} grains`}
            >
              {th.label}
            </button>
          ))}
        </div>
      </Section>

      <Section title={t('explore.types')}>
        <div className="flex flex-wrap gap-1.5">
          {data.topTypes.map((ty) => (
            <button
              key={ty.type}
              onClick={() => nav(`#/?type=${encodeURIComponent(ty.type)}`)}
              className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-muted"
            >
              {ty.type} <span className="text-fg">· {ty.n}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title={t('explore.ownership')}>
        <div className="flex flex-wrap gap-1.5">
          {data.ownership.map((o) => (
            <Chip key={o.ownership}>
              {t(`ownership.${o.ownership}`)} · {o.n}
            </Chip>
          ))}
        </div>
      </Section>

      <OpenQuestions onAsk={onCapture} />
      <Chronicle />
      <Contradictions onCapture={onCapture} />
    </div>
  );
}

/* ---------- détail (thème ou entité) ---------- */
function DetailView({ route }: { route: { view: 'theme' | 'entity' | 'grain' | 'author'; id: string } }) {
  const { t } = useTranslation();
  const [data, setData] = useState<ThemeDetail | EntityDetail | null>(null);
  const [error, setError] = useState(false);
  const [sheet, setSheet] = useState<string | null>(null);
  const [synthing, setSynthing] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    setSheet(null);
    const p = route.view === 'theme' ? api.browse.theme(route.id) : api.browse.entity(route.id);
    p.then((d) => {
      const data = d as ThemeDetail | EntityDetail;
      setData(data);
      setSheet(data.kind === 'theme' ? data.theme.sheet : data.entity.sheet);
    }).catch(() => setError(true));
  }, [route.view, route.id]);

  // Sépare la fiche de ses sources encodées (FIC-02).
  const { md, sources } = useMemo(() => parseSheet(sheet), [sheet]);

  const doExport = useCallback(() => {
    if (!data) return;
    // On exporte la fiche nettoyée (sans le commentaire technique des sources).
    if (data.kind === 'theme') downloadMarkdown(`theme-${data.theme.label}`, themeMarkdown({ ...data, theme: { ...data.theme, sheet: md } }));
    else downloadMarkdown(data.entity.name, entityMarkdown({ ...data, entity: { ...data.entity, sheet: md } }));
  }, [data, md]);

  const doSynth = useCallback(async () => {
    if (!data) return;
    setSynthing(true);
    try {
      const r = data.kind === 'theme' ? await api.browse.synthTheme(data.theme.id) : await api.browse.synthEntity(data.entity.id);
      setSheet(r.sheet);
    } catch {
      /* silencieux */
    } finally {
      setSynthing(false);
    }
  }, [data]);

  if (error) return <p className="text-muted">{t('error.generic')}</p>;
  if (!data) return <Spinner label={t('explore.loading')} />;

  const title = data.kind === 'theme' ? `# ${data.theme.label}` : data.entity.name;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          {data.kind === 'entity' && (
            <p className="mt-1 text-sm text-muted">
              {data.entity.type} · {t(`ownership.${data.entity.ownership}`)}
            </p>
          )}
          <p className="mt-0.5 text-sm text-muted">{t('explore.grainCount', { count: data.grains.length })}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => void doSynth()}
            disabled={synthing}
            className="flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-1.5 text-sm text-fg hover:bg-accent hover:text-accent-fg disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" /> {synthing ? t('explore.synthLoading') : t('explore.synth')}
          </button>
          <button
            onClick={doExport}
            className="flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-1.5 text-sm text-fg hover:bg-accent hover:text-accent-fg"
          >
            <Download className="h-4 w-4" /> {t('explore.export')}
          </button>
          <ShareButton />
        </div>
      </div>

      {(sheet || synthing) && (
        <Section title={t('explore.summary')}>
          {synthing && !sheet ? (
            <Spinner label={t('explore.synthLoading')} />
          ) : (
            <div className="space-y-2">
              <div className="md rounded-lg bg-surface-2 px-3 py-2 text-sm leading-relaxed ring-1 ring-border">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
              </div>
              {sources.length > 0 && (
                <details className="group/src">
                  <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted hover:text-fg">
                    <ChevronRight className="h-3.5 w-3.5 transition-transform group-open/src:rotate-90" />
                    {t('explore.sources')} · {sources.length}
                  </summary>
                  <div className="mt-1.5 space-y-1.5">
                    {sources.map((s) => (
                      <details key={s.n} className="group rounded-lg border border-border bg-surface px-3 py-2">
                        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
                          <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-accent/15 text-xs text-accent">
                            {s.n}
                          </span>
                          <span className="truncate">{s.title}</span>
                        </summary>
                        <p className="mt-2 text-sm text-muted">{s.excerpt}…</p>
                      </details>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </Section>
      )}

      {data.kind === 'theme' ? (
        <>
          {data.entities.length > 0 && (
            <Section title={t('explore.relatedEntities')}>
              <EntityChips entities={data.entities} />
            </Section>
          )}
          {data.relatedThemes.length > 0 && (
            <Section title={t('explore.relatedThemes')}>
              <ThemeChips themes={data.relatedThemes} />
            </Section>
          )}
        </>
      ) : (
        <>
          {data.themes.length > 0 && (
            <Section title={t('explore.themesOf')}>
              <ThemeChips themes={data.themes} />
            </Section>
          )}
          {data.relatedEntities.length > 0 && (
            <Section title={t('explore.relatedEntities')}>
              <EntityChips entities={data.relatedEntities} />
            </Section>
          )}
        </>
      )}

      <Section title={t('explore.grains')}>
        <GrainList grains={data.grains} />
      </Section>
    </div>
  );
}

/* ---------- recherche ---------- */
function SearchResults({ q }: { q: string }) {
  const { t } = useTranslation();
  const [themes, setThemes] = useState<ThemeRow[]>([]);
  const [entities, setEntities] = useState<EntityRow[]>([]);
  useEffect(() => {
    let alive = true;
    const id = setTimeout(() => {
      void api.browse.themes(q).then((r) => alive && setThemes(r)).catch(() => {});
      void api.browse.entities(q).then((r) => alive && setEntities(r)).catch(() => {});
    }, 200);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [q]);

  if (themes.length === 0 && entities.length === 0) return <p className="text-muted">{t('explore.noResult')}</p>;
  return (
    <div className="space-y-5">
      {themes.length > 0 && (
        <Section title={t('explore.resultsThemes')}>
          <ThemeChips themes={themes} />
        </Section>
      )}
      {entities.length > 0 && (
        <Section title={t('explore.resultsEntities')}>
          <EntityChips entities={entities} />
        </Section>
      )}
    </div>
  );
}

/* ---------- vue Explorer ---------- */
export function Explore({ onCapture }: { onCapture: OnCapture }) {
  const { t } = useTranslation();
  const route = useHashRoute();
  const [q, setQ] = useState('');

  // Ouvrir une fiche depuis la recherche vide le champ → on voit la page, pas la liste.
  useEffect(() => {
    if (route.view !== 'overview') setQ('');
  }, [route.view, route.id]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 pb-3">
        <button
          onClick={() => nav('#/')}
          className={`flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm ${
            route.view === 'overview' && !q ? 'bg-surface-2 text-fg' : 'bg-surface-2 text-muted hover:text-fg'
          }`}
          title={t('explore.home')}
          aria-label={t('explore.home')}
        >
          <Compass className="h-4 w-4" /> <span className="hidden sm:inline">{t('explore.home')}</span>
        </button>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('explore.searchPlaceholder')}
          className="flex-1 rounded-xl bg-surface px-4 py-2 text-sm ring-1 ring-border outline-none placeholder:text-muted focus:ring-accent"
        />
      </div>

      <div className="flex-1 overflow-y-auto pb-6">
        {q.trim() ? (
          <SearchResults q={q.trim()} />
        ) : route.view === 'overview' ? (
          <OverviewView onCapture={onCapture} />
        ) : route.view === 'grain' ? (
          <GrainView id={route.id} />
        ) : route.view === 'author' ? (
          <AuthorView id={route.id} />
        ) : (
          <DetailView route={route} />
        )}
      </div>
    </div>
  );
}
